import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSignedUrl, useSignedUrls } from '../supabase/signedMedia';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { IconX, IconWarning, IconArrowUpRight, IconHeart, IconChat } from './icons';

export interface PostViewerData {
  id?: string;
  post_type?: string | null;
  thumb_url?: string | null;
  video_url?: string | null;
  child_posts?: unknown;
  caption?: string | null;
  likes?: number | null;
  comments?: number | null;
  posted_at?: string | null;
  permalink?: string | null;
  handle?: string | null;
  brand_name?: string | null;
  advertiser_name?: string | null;
}

interface Props {
  post: PostViewerData;
  onClose: () => void;
}

/** Extract carousel slide image URLs from child_posts JSON. */
function extractCarouselSlides(childPosts: unknown): string[] {
  if (!childPosts) return [];
  if (typeof childPosts === 'object' && childPosts !== null && Array.isArray((childPosts as { slides?: unknown[] }).slides)) {
    const out: string[] = [];
    for (const s of (childPosts as { slides: Array<Record<string, unknown>> }).slides) {
      const u = (s.public_url ?? s.url) as string | undefined;
      if (u) out.push(u);
    }
    return out;
  }
  if (typeof childPosts === 'object' && childPosts !== null && Array.isArray((childPosts as { images?: unknown[] }).images)) {
    return (childPosts as { images: unknown[] }).images.filter((u): u is string => typeof u === 'string');
  }
  if (Array.isArray(childPosts)) {
    const out: string[] = [];
    for (const slide of childPosts) {
      if (!slide || typeof slide !== 'object') continue;
      const s = slide as Record<string, unknown>;
      const u = (s.public_url ?? s.displayUrl ?? s.imageUrl ?? s.thumbnailUrl ?? s.url) as string | undefined;
      if (u) out.push(u);
    }
    return out;
  }
  return [];
}

export default function PostViewerModal({ post, onClose }: Props) {
  const isCarousel = post.post_type === 'carousel';
  const isVideo = post.post_type === 'reel' || post.post_type === 'video';
  const slides = isCarousel ? extractCarouselSlides(post.child_posts) : [];
  const allImages = slides.length > 0 ? slides : (post.thumb_url ? [post.thumb_url] : []);

  // Sign private-bucket media (passthrough for raw IG CDN URLs).
  const signedImages = useSignedUrls(allImages);
  const signedVideo = useSignedUrl(post.video_url);
  const signedPoster = useSignedUrl(post.thumb_url);

  const [idx, setIdx] = useState(0);
  const [imgErrored, setImgErrored] = useState(false);

  useEffect(() => { setImgErrored(false); }, [idx]);

  useBodyScrollLock();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (isCarousel && e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      else if (isCarousel && e.key === 'ArrowRight') setIdx((i) => Math.min(allImages.length - 1, i + 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [allImages.length, onClose, isCarousel]);

  const curRaw = allImages[Math.min(idx, allImages.length - 1)] ?? null;
  const cur = curRaw ? (signedImages[curRaw] ?? curRaw) : null;
  const isMirrored = curRaw ? /supabase\.co/i.test(curRaw) : false;

  const overlay = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.92)', zIndex: 2147483647,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        style={{
          position: 'absolute', top: 16, right: 16,
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff', width: 36, height: 36, borderRadius: 18, cursor: 'pointer',
          fontSize: 'var(--fs-h2)', lineHeight: 1, fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      ><IconX size={18} color="#fff" /></button>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex', gap: 24, width: '100%', maxWidth: 1100,
          maxHeight: '90vh', alignItems: 'center',
        }}
      >
        {/* Left: media (carousel / video / image) */}
        <div style={{ flex: '0 1 700px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isCarousel && idx > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setIdx((i) => Math.max(0, i - 1)); }}
              aria-label="Prev"
              style={{
                position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', width: 44, height: 44, borderRadius: 22, cursor: 'pointer',
                fontSize: 'var(--fs-h1)', lineHeight: 1, fontFamily: 'inherit', zIndex: 2,
              }}
            >‹</button>
          )}
          {isCarousel && idx < allImages.length - 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setIdx((i) => Math.min(allImages.length - 1, i + 1)); }}
              aria-label="Next"
              style={{
                position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', width: 44, height: 44, borderRadius: 22, cursor: 'pointer',
                fontSize: 'var(--fs-h1)', lineHeight: 1, fontFamily: 'inherit', zIndex: 2,
              }}
            >›</button>
          )}

          {isVideo && post.video_url ? (
            <video
              src={signedVideo ?? post.video_url}
              poster={signedPoster ?? post.thumb_url ?? undefined}
              controls
              autoPlay
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: 8, background: '#000' }}
            />
          ) : cur && !imgErrored ? (
            <img
              src={cur}
              alt=""
              referrerPolicy="no-referrer"
              onError={() => setImgErrored(true)}
              style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8, background: '#111' }}
            />
          ) : (
            <div style={{
              maxWidth: 480, padding: 24, borderRadius: 10,
              background: 'rgba(20,22,30,0.95)', border: '1px solid rgba(255,255,255,0.1)',
              color: 'var(--text)', textAlign: 'center', fontFamily: 'inherit',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><IconWarning size={32} /></div>
              <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', marginBottom: 6 }}>Media unavailable</div>
              <div style={{ fontSize: 'var(--fs-small)', color: 'var(--muted)', lineHeight: 1.5, marginBottom: 14 }}>
                {isMirrored
                  ? 'Mirrored copy failed to load.'
                  : 'Original Instagram CDN URL has expired.'}
              </div>
              {post.permalink && (
                <a
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block', padding: '6px 14px', borderRadius: 6,
                    background: 'rgba(74,158,255,0.15)', color: 'var(--accent-text)',
                    textDecoration: 'none', fontSize: 'var(--fs-small)', border: '1px solid rgba(74,158,255,0.3)',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>View on Instagram <IconArrowUpRight size={13} /></span>
                </a>
              )}
            </div>
          )}

          {isCarousel && allImages.length > 1 && (
            <div style={{
              position: 'absolute', bottom: -24, left: '50%', transform: 'translateX(-50%)',
              color: 'var(--text)', fontSize: 'var(--fs-small)', background: 'rgba(0,0,0,0.5)',
              padding: '3px 10px', borderRadius: 12, fontFamily: 'inherit',
            }}>
              {idx + 1} / {allImages.length}
            </div>
          )}
        </div>

        {/* Right: meta panel */}
        <aside style={{
          flex: '1 1 320px', maxWidth: 380,
          background: 'rgba(20,22,30,0.95)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10, padding: 18, color: 'var(--text)', fontFamily: 'inherit',
          maxHeight: '85vh', overflowY: 'auto',
        }}>
          <div style={{ fontSize: 'var(--fs-caption)', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--muted)', marginBottom: 4 }}>
            {post.post_type ? post.post_type.toUpperCase() : 'POST'}
            {post.handle && <> · @{post.handle}</>}
          </div>
          {(post.brand_name || post.advertiser_name) && (
            <div style={{ fontSize: 'var(--fs-body)', color: 'var(--accent-text)', marginBottom: 8 }}>
              {post.brand_name ?? post.advertiser_name}
              {post.brand_name && post.advertiser_name && post.brand_name !== post.advertiser_name && (
                <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-caption)' }}> · {post.advertiser_name}</span>
              )}
            </div>
          )}
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--muted)', marginBottom: 12 }}>
            {post.posted_at ? new Date(post.posted_at).toLocaleString() : '—'}
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 'var(--fs-body)', marginBottom: 14, color: 'var(--text-2)' }}>
            {post.likes != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconHeart size={14} /> {post.likes.toLocaleString()}</span>}
            {post.comments != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconChat size={14} /> {post.comments.toLocaleString()}</span>}
          </div>
          {post.caption && (
            <div style={{ fontSize: 'var(--fs-body)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-2)' }}>
              {post.caption}
            </div>
          )}
          {post.permalink && (
            <a
              href={post.permalink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                marginTop: 16, padding: '6px 12px',
                borderRadius: 6, background: 'rgba(74,158,255,0.12)',
                color: 'var(--accent-text)', textDecoration: 'none', fontSize: 'var(--fs-small)',
                border: '1px solid rgba(74,158,255,0.3)',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              View on Instagram <IconArrowUpRight size={13} />
            </a>
          )}
        </aside>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
